'use client';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { colorBrightness } from '@/packages/color/colorBrightness';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import type { HeadlineChannel, ListenOption } from '@/packages/sxm/sports/channelWaterfall';
import { EMPTY_CHANNEL } from '../BroadcastGrid/broadcastRows';
import { ListenOptions, type ListenOptionsProps } from '../ListenOptions/ListenOptions';
import styles from './gameCard.module.scss';

const HALF_LUMINANCE = 255 / 2;

export interface Team {
    image: React.ComponentProps<typeof ImageWrapper>;
    title: string;
    subtitle?: string;
    backgroundColor: string;
    link: React.ComponentProps<typeof Link>;
    headline?: HeadlineChannel;
    listenOptions?: ListenOption[];
}

export type GameCardTeamTupleType = [Team, Team?];

export interface GameCardProps {
    teams: GameCardTeamTupleType;
    dateTime: Date;
    link?: React.ComponentProps<typeof Link>;
    linkText?: string;
    firstCopyright?: string;
    secondCopyright?: string;
    useContrastingImageColors?: boolean;
}

const SIDES = ['left', 'right'] as const;
const LAYOUTS = {
    false: { image: styles.single, info: styles.singleInfo, infoCol: '', infoRow: '' },
    true: { image: styles.dual, info: styles.dualInfo, infoCol: styles.teamsInfoCol, infoRow: styles.teamsInfoRow },
};
const SIDE_STYLES = {
    left: { image: styles.leftImage, title: styles.leftTitle, link: styles.hoverDetectorLeft },
    right: { image: styles.rightImage, title: styles.rightTitle, link: styles.hoverDetectorRight },
};
const HOME_AWAY = /^(home|away)$/i;

function getDateTime(date: Date) {
    const dt = dayjs(date);
    const timezone = new Date(date).toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
    return `${dt.format('ddd, MMM D').toUpperCase()} · ${dt.format('h:mm A')} ${timezone}`;
}

function iconColorClass(team: Team, index: number) {
    let tone = 'White';
    if (colorBrightness(team.backgroundColor) > HALF_LUMINANCE) tone = 'Black';
    return styles[`team${index + 1}Icon${tone}`];
}

function TeamChannel({
    team,
    single,
    dataCy,
    popoverAnchor,
    popoverAnchorRef,
}: {
    team?: Team;
    single: boolean;
    dataCy?: string;
    popoverAnchor: ListenOptionsProps['popoverAnchor'];
    popoverAnchorRef: ListenOptionsProps['popoverAnchorRef'];
}) {
    if (!team?.headline) return <span className={styles.emptyChannel}>{EMPTY_CHANNEL}</span>;

    return (
        <div className={cn(styles.channel, { [styles.singleChannel]: single })}>
            <ListenOptions
                entityName={team.title}
                headline={team.headline}
                options={team.listenOptions ?? []}
                dataCy={dataCy}
                popoverAnchor={popoverAnchor}
                popoverAnchorRef={popoverAnchorRef}
            />
        </div>
    );
}

function Subtitle({ subtitle }: { subtitle?: string }) {
    if (!subtitle || HOME_AWAY.test(subtitle)) return undefined;
    return <Text tag="p">{subtitle}</Text>;
}

function Copyright({ first = '', second = '' }: { first?: string; second?: string }) {
    if (!first && !second) return undefined;

    let separator = '';
    if (first && second.trim()) separator = '; ';
    return <div className={styles.copyright}>{`${first}${separator}${second}`}</div>;
}

export function GameCard({
    teams,
    dateTime,
    link,
    linkText,
    firstCopyright,
    secondCopyright,
    useContrastingImageColors,
}: GameCardProps) {
    const [calculatedDateTime, setCalculatedDateTime] = useState('');
    const cardRef = useRef<HTMLDivElement>(null);
    const cardId = useId();
    const cardAnchorName = `--game-card-${cardId.replace(/[^a-zA-Z0-9-]/g, '')}`;
    const popoverAnchor = {
        name: cardAnchorName,
        horizontalInset: 16,
        gap: -8,
    };
    const presentTeams = teams.filter((team) => team !== undefined);
    const isDual = presentTeams.length === 2;
    const layout = LAYOUTS[`${isDual}`];
    const style = {
        '--background1': teams[0].backgroundColor,
        '--background2': teams[1]?.backgroundColor,
        anchorName: cardAnchorName,
    };

    const iconClasses = presentTeams.map(iconColorClass).filter(() => useContrastingImageColors);

    useEffect(() => {
        setCalculatedDateTime(getDateTime(dateTime));
    }, [dateTime]);

    return (
        <div className={styles.wrapper} data-cy="game-card">
            <div ref={cardRef} className={styles.container} style={style as CSSProperties}>
                {presentTeams.map((team, index) => (
                    <Link
                        key={SIDES[index]}
                        {...team.link}
                        className={cn({ [SIDE_STYLES[SIDES[index]].link]: isDual, [styles.singleLink]: !isDual })}
                    />
                ))}
                <div className={cn(styles.imageRow, layout.image, iconClasses)}>
                    {presentTeams.map((team, index) => (
                        <div key={SIDES[index]}>
                            <ImageWrapper
                                src={team.image.src}
                                alt={team.image.alt || ''}
                                ariaLabel={team.image.ariaLabel}
                                ariaHidden={team.image.ariaHidden}
                                className={cn({ [SIDE_STYLES[SIDES[index]].image]: isDual })}
                            />
                        </div>
                    ))}
                </div>
                <Copyright first={firstCopyright} second={secondCopyright} />
                <div className={cn(styles.infoCol, layout.infoCol)}>
                    <div className={styles.date}>{calculatedDateTime}</div>
                    <div className={cn(styles.infoRow, layout.infoRow)}>
                        {presentTeams.map((team, index) => (
                            <div className={layout.info} key={SIDES[index]}>
                                <div>
                                    <Text tag="h6" className={cn({ [SIDE_STYLES[SIDES[index]].title]: isDual })}>
                                        {team.title}
                                    </Text>
                                    <Subtitle subtitle={team.subtitle} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.cardChannel}>
                        {presentTeams.map((team, index) => (
                            <TeamChannel
                                key={SIDES[index]}
                                team={team}
                                single={!isDual}
                                dataCy={`game-card-channel-${SIDES[index]}`}
                                popoverAnchor={popoverAnchor}
                                popoverAnchorRef={cardRef}
                            />
                        ))}
                    </div>
                </div>
            </div>
            {link && (
                <div className={styles.bottomLink}>
                    <Link {...link}>{linkText}</Link>
                </div>
            )}
        </div>
    );
}
